import { useGesture } from '@use-gesture/react'
import { useHasRunFirstRender } from 'hooks/useHasRunFirstRender'
import { Virtualizer } from 'lib/react-virtual/useVirtual'
import { Post } from 'models/Post'
import { MutableRefObject, useCallback, useEffect } from 'react'

export interface UseRestoreScrollProps {
  scrollContainerRef: MutableRefObject<HTMLElement | null>
  rowVirtualizer: Virtualizer
  disabled?: boolean
  feedItems: Post[]
  scrollIndex: number
  setScrollIndex?: (index: number) => void
  scrollToAlign?: 'start' | 'center' | 'end'
}

export const useRestoreScroll = ({
  disabled,
  rowVirtualizer,
  scrollContainerRef,
  scrollIndex,
  feedItems,
  setScrollIndex,
  scrollToAlign = 'start'
}: UseRestoreScrollProps) => {
  const virtualItems = rowVirtualizer.getVirtualItems()

  // This effect restores scroll position when returning to the virtalized feed, should run on
  // first render only.
  const hasRunFirstRender = useHasRunFirstRender()
  useEffect(() => {
    if (!(disabled || hasRunFirstRender)) {
      if (scrollIndex >= 0) {
        setTimeout(() => rowVirtualizer.scrollToIndex(scrollIndex, { align: scrollToAlign }), 100)
      } else if (scrollContainerRef?.current?.scrollTop) {
        scrollContainerRef.current.scrollTop = 0
      }
    }
  }, [rowVirtualizer, scrollIndex, disabled, scrollContainerRef, hasRunFirstRender, scrollToAlign])

  // Watch for hard reset of scroll index and reset the scroll position.
  useEffect(() => {
    if (scrollIndex < 0) {
      setTimeout(() => rowVirtualizer.scrollToIndex(scrollIndex, { align: scrollToAlign }), 0)
      setScrollIndex?.(0)
    }
  }, [scrollIndex, rowVirtualizer, setScrollIndex, scrollContainerRef, scrollToAlign])

  const handleScroll = useCallback(() => {
    if (feedItems && scrollContainerRef.current) {
      const scrollTop = scrollContainerRef.current.scrollTop
      const centerOfScreen = scrollTop + window.innerHeight / 2
      const itemCenters = virtualItems.map((item) => (item.end - item.start) / 2 + item.start)
      const closestIndex = itemCenters?.reduce((prev, curr) =>
        Math.abs(curr - centerOfScreen) < Math.abs(prev - centerOfScreen) ? curr : prev
      )
      const newScrollIndex = virtualItems[itemCenters.indexOf(closestIndex)].index

      if (scrollIndex !== newScrollIndex) {
        setScrollIndex?.(newScrollIndex)
      }
    }
  }, [feedItems, virtualItems, scrollContainerRef, scrollIndex, setScrollIndex])

  useGesture(
    {
      onWheelEnd: (_state) => handleScroll(),
      onTouchEnd: (_state) => handleScroll()
    },
    { eventOptions: { capture: true }, target: scrollContainerRef }
  )
}
